import { createFileRoute, Navigate, Outlet } from '@tanstack/react-router';
import { Suspense, useState } from 'react';
import { LoadingComponent } from '@/components/shared-ui/loading-component';
import { PlayBar } from '@/components/start/play-bar/play-bar';
import { GlobalDropzone } from '@/components/web-fe/design-v2/global-dropzone';
import { SidebarV2 } from '@/components/web-fe/design-v2/sidebar-v2';
import { TopBarV2 } from '@/components/web-fe/design-v2/top-bar-v2';
import { ModalCmdKProvider } from '@/components/web-fe/dialogs/modal-cmd-k/modal-cmd-k';
import { PremiumDialogsProvider } from '@/components/web-fe/misc/providers/premium-dialog-provider';
import { UploadDialogsProvider } from '@/components/web-fe/misc/providers/upload-dialog-providers';
import { SearchStateContext } from '@/hooks/settings.hooks';
import { useIsRouteActive } from '@/hooks/use-is-route-active';
import { useFreePlan } from '@/hooks/use-free-plan-availability';
import { useTrackPlayTime } from '@/hooks/use-track-audio';
import { isSSR } from '@/env';
import { useIsInvalidSubscription } from '@/services/subscription-service';
import { useUserSessionInfo } from '@/services/user-service';
import { rootLogger } from '@/lib/root-logger';

const moduleLogger = rootLogger.child({ module: '_dashboard' });

export const Route = createFileRoute('/app/_authenticated/_dashboard')({
  component: Dashboard,
});

function Dashboard() {
  const isSubscriptionInvalid = useIsInvalidSubscription();
  const { isAvailable: isFreePlanAvailable, isResolvingCountry } =
    useFreePlan();
  const searchState = useState('');
  const userSessionInfo = useUserSessionInfo();

  // No auth gate here on purpose. `_authenticated`'s `beforeLoad` is the single
  // gate for this subtree (TanStack's documented pattern) and has already run
  // for this navigation with an authoritative session read. Re-deriving it from
  // `authClient.useSession()` bounced a just-signed-in user back to the login
  // page, because that store can still be reporting the previous session.
  // @see test/e2e/flows/reauth-after-logout.spec.ts

  const hasResolvedUser = userSessionInfo.isLoggedIn;

  if (
    hasResolvedUser &&
    isSubscriptionInvalid &&
    !isFreePlanAvailable &&
    !isResolvingCountry &&
    !isSSR
  ) {
    moduleLogger.info(
      { email: userSessionInfo.email },
      'User subscription is invalid, redirecting to premium',
    );
    return <Navigate to='/app/premium' />;
  }
  return (
    <SearchStateContext value={searchState}>
      <PremiumDialogsProvider>
        <UploadDialogsProvider>
          <DashboardShell />
        </UploadDialogsProvider>
      </PremiumDialogsProvider>
    </SearchStateContext>
  );
}

function DashboardShell() {
  // Routes that bring their own toolbar: stacking the shell's top bar above
  // one cuts into the page for no gain. Only that bar is dropped — the sidebar,
  // play bar and every provider stay.
  const isReader = useIsRouteActive({
    to: '/app/readalong/$audioIdOrSlug',
    fuzzy: true,
  });
  const isEditor = useIsRouteActive({ to: '/app/text-to-audio', fuzzy: true });
  const hasOwnToolbar = isReader || isEditor;

  return (
    <ModalCmdKProvider>
      <div
        style={{ fontFamily: 'var(--v2-font)' }}
        className='flex size-full flex-col bg-[var(--v2-app-bg)] text-[var(--v2-text-primary)] subpixel-antialiased'
      >
        <GlobalDropzone>
          <div className='flex grow overflow-hidden'>
            <div className='hidden lg:block'>
              <SidebarV2 />
            </div>
            <main className='relative flex h-full min-w-0 grow flex-col overflow-hidden bg-[var(--v2-app-bg)]'>
              <PlayTimeWatcher />
              {!hasOwnToolbar && <TopBarV2 />}
              <div className='min-h-0 grow overflow-hidden'>
                {/* Route pages prefetch via loaders and read via
                    useSuspenseQuery; this Suspense catches the suspense
                    fallback. Query FAILURES are caught per-route by each
                    route's `errorComponent: DashboardQueryError`, which renders
                    at this Outlet slot and auto-resets on navigation — a
                    layout-level boundary here would not. */}
                <Suspense fallback={<LoadingComponent />}>
                  <Outlet />
                </Suspense>
              </div>
            </main>
          </div>
          <PlayBar className='shadow-none' />
        </GlobalDropzone>
      </div>
    </ModalCmdKProvider>
  );
}

function PlayTimeWatcher() {
  useTrackPlayTime();

  return null;
}
